import GridPill from "@/bizminer/App/PillarInsights/ImprovementLevers/_components/GridPill.tsx";
import {
  EFFORT_LEVELS,
  IMPACT_LEVELS,
  LEVEL_LABELS,
} from "@/bizminer/App/PillarInsights/ImprovementLevers/shared/improvementLeverUtils.ts";
import { Card } from "@/components/ui/card";
import type { BizminerImprovementLever } from "@/types/serializers";

interface GridViewProps {
  improvementLevers: BizminerImprovementLever[];
  onSelect: (lever: BizminerImprovementLever) => void;
}

function groupByCell(
  levers: BizminerImprovementLever[],
): Record<string, BizminerImprovementLever[]> {
  const grouped: Record<string, BizminerImprovementLever[]> = {};

  for (const impact of IMPACT_LEVELS) {
    for (const effort of EFFORT_LEVELS) {
      grouped[`${impact}-${effort}`] = [];
    }
  }

  for (const lever of levers) {
    const key = `${lever.impact}-${lever.effort}`;
    if (grouped[key]) {
      grouped[key].push(lever);
    }
  }

  return grouped;
}

function GridLegend() {
  return (
    <Card className="flex flex-row flex-wrap items-center gap-6 p-3">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">
        Legend:
      </span>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-emerald-500" />
        <span className="text-sm text-muted-foreground">Quick Wins</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-amber-500" />
        <span className="text-sm text-muted-foreground">
          Strategic Initiatives
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-blue-500" />
        <span className="text-sm text-muted-foreground">
          Growth Opportunities
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-orange-500" />
        <span className="text-sm text-muted-foreground">
          Long-term Foundations
        </span>
      </div>
    </Card>
  );
}

interface GridProps {
  groupedLevers: Record<string, BizminerImprovementLever[]>;
  onSelect: (lever: BizminerImprovementLever) => void;
}

function Grid({ groupedLevers, onSelect }: GridProps) {
  return (
    <Card className="overflow-hidden">
      <div className="grid grid-cols-[80px_repeat(3,1fr)]">
        <div className="bg-muted px-3 py-2 border-b border-r border-border" />
        {EFFORT_LEVELS.map((effort) => (
          <div
            key={effort}
            className="bg-muted px-3 py-2 border-b border-border text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide"
          >
            {LEVEL_LABELS[effort]} Effort
          </div>
        ))}
      </div>

      <div
        className="grid"
        style={{
          gridTemplateColumns: "80px repeat(3, 1fr)",
          gridTemplateRows: "repeat(3, auto)",
        }}
      >
        {IMPACT_LEVELS.map((impact) => {
          const rowHasItems = EFFORT_LEVELS.some((effort) => {
            return (groupedLevers[`${impact}-${effort}`] || []).length > 0;
          });
          const rowMinHeight = rowHasItems ? "min-h-[112px]" : "min-h-[56px]";

          return (
            <div key={impact} className="contents">
              <div
                className={`bg-muted p-3 border-r border-b border-border flex items-center justify-center ${rowMinHeight}`}
              >
                <span
                  className="text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                  style={{
                    writingMode: "vertical-lr",
                    transform: "rotate(180deg)",
                  }}
                >
                  {LEVEL_LABELS[impact]} Impact
                </span>
              </div>

              {EFFORT_LEVELS.map((effort) => {
                const cellKey = `${impact}-${effort}`;
                const levers = groupedLevers[cellKey] || [];

                return (
                  <div
                    key={cellKey}
                    className={`p-3 border-b border-r border-border last:border-r-0 flex flex-col gap-2 ${rowMinHeight}`}
                  >
                    {levers.map((lever) => (
                      <GridPill
                        key={lever.id}
                        improvementLever={lever}
                        onClick={() => onSelect(lever)}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default function GridView({
  improvementLevers,
  onSelect,
}: GridViewProps) {
  const groupedLevers = groupByCell(improvementLevers);

  return (
    <>
      <Grid groupedLevers={groupedLevers} onSelect={onSelect} />
      <GridLegend />
    </>
  );
}
